import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { comics, categories } from '@/mocks/comics';
import ComicCard from '@/components/feature/ComicCard';
import PageHead from '@/components/base/PageHead';

export default function Category() {
  const [activeCategory, setActiveCategory] = useState('all');
  const [sortBy, setSortBy] = useState<'popular' | 'latest'>('popular');

  const filteredComics = useMemo(() => {
    let filtered = activeCategory === 'all'
      ? [...comics]
      : comics.filter((c) => c.categorySlug === activeCategory);

    if (sortBy === 'popular') {
      filtered.sort((a, b) => b.views - a.views);
    } else {
      filtered.sort((a, b) => new Date(b.updateTime).getTime() - new Date(a.updateTime).getTime());
    }

    return filtered;
  }, [activeCategory, sortBy]);

  const activeCategoryName = categories.find((c) => c.slug === activeCategory)?.name || '全部';

  return (
    <>
      <PageHead
        title="分类浏览 - 韩国漫画网 | 韩国漫画网站大全 分类韩漫"
        description="韩国漫画网分类浏览页面，按恋爱、动作、奇幻、悬疑、搞笑等分类免费韩漫在线看，提供下拉式阅读体验，实时追更热门漫画。"
        keywords="韩国漫画网站大全, 分类韩漫, 韩国漫画分类, 免费韩漫在线看, 韩漫分类浏览"
        canonicalUrl="https://www.koreancomic.cn/category/all"
      />

      <main>
        {/* Page Header */}
        <section className="px-4 md:px-6 py-8 md:py-12 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-2 text-sm text-foreground-500 mb-2">
              <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">首页</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-700 font-medium">分类浏览</span>
            </div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
              <strong>韩国漫画网站大全</strong> 分类浏览
            </h1>
            <p className="text-foreground-500 text-sm mt-2">
              按分类找到你想看的<strong>免费韩漫在线看</strong>，支持下拉式阅读
            </p>
          </div>
        </section>

        {/* Category Tabs */}
        <section className="sticky top-16 z-40 bg-background-50/95 backdrop-blur-md border-b border-background-200/70">
          <div className="px-4 md:px-6">
            <div className="max-w-7xl mx-auto flex items-center gap-1 overflow-x-auto py-3 scrollbar-hide">
              {categories.map((cat) => (
                <button
                  key={cat.slug}
                  onClick={() => setActiveCategory(cat.slug)}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                    activeCategory === cat.slug
                      ? 'bg-primary-500 text-white'
                      : 'text-foreground-600 hover:text-foreground-950 hover:bg-background-100'
                  }`}
                >
                  <i className={`${cat.icon} text-xs`}></i>
                  {cat.name}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Content */}
        <section className="px-4 md:px-6 py-8 md:py-10">
          <div className="max-w-7xl mx-auto">
            {/* Sort & Results Info */}
            <div className="flex items-center justify-between mb-6">
              <p className="text-sm text-foreground-500">
                <span className="font-semibold text-foreground-950">{activeCategoryName}</span>
                {' '}共 <span className="font-semibold text-primary-600">{filteredComics.length}</span> 部作品
              </p>
              <div className="flex items-center gap-1 bg-background-100 rounded-full p-1">
                <button
                  onClick={() => setSortBy('popular')}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                    sortBy === 'popular'
                      ? 'bg-white text-foreground-950'
                      : 'text-foreground-500 hover:text-foreground-700'
                  }`}
                >
                  最受欢迎
                </button>
                <button
                  onClick={() => setSortBy('latest')}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                    sortBy === 'latest'
                      ? 'bg-white text-foreground-950'
                      : 'text-foreground-500 hover:text-foreground-700'
                  }`}
                >
                  最近更新
                </button>
              </div>
            </div>

            {/* Comic Grid */}
            {filteredComics.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-5">
                {filteredComics.map((comic) => (
                  <ComicCard key={comic.id} comic={comic} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-background-100 mx-auto mb-4">
                  <i className="ri-emotion-sad-line text-2xl text-foreground-300"></i>
                </div>
                <p className="text-foreground-500">该分类暂无作品</p>
              </div>
            )}

            {/* Load More */}
            {filteredComics.length > 0 && (
              <div className="text-center mt-10">
                <button className="px-8 py-3 rounded-full bg-background-100 text-foreground-600 text-sm font-medium hover:bg-background-200 transition-colors cursor-pointer">
                  加载更多
                </button>
              </div>
            )}
          </div>
        </section>
      </main>
    </>
  );
}